import React, {useContext, useEffect, useState} from 'react';
import {
    Stepper,
    Step,
    StepLabel,
    Button,
    Autocomplete,
    TextField,
    Backdrop,
    CircularProgress,
    Typography, Box
} from '@mui/material';
import {
    capitalizeFirstLetter,
    deepClone, getQueryParams,
    getQueryParamValue,
    isMobile,
    navigateKBSubdomain,
    parseJSON
} from "../../App/utils";
import {saveKB} from "../../API/KB/KBAPI";
import {encrypt, encryptKBItem, generateKey} from "../../App/cryptoUtils";
import {AlertContext} from "../../App/App";
import {downloadApp, listPublicAppFiles} from "../../API/APP/AppAPI";
import {
    buildNodePackage,
    buildWebpackPackage,
    cloneFiles,
    copyAppImage,
    copyFile,
    createSecret
} from "../../API/KB/Files";
import {extractModuleNames} from "../../Code/utils";
import { styled } from '@mui/material/styles';
import Check from '@mui/icons-material/Check';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import {colorSecondary} from "../../App/theme";
import {TypingLabel} from "../../Components/TypingLabel";
import {appServiceTypes, ChatModels, interfaces, MyServices} from "../../constants";
import {inputTools as InputTools} from "../../InputTools/constant";
import {parseJSONTool} from "../../utils";
import {PHONE_VERIFICATION_STATUS} from "../../API/User";
import {SecretInput} from "./SecretInput";
import ReactMarkdown from 'react-markdown';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const QontoConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 10,
        left: 'calc(-50% + 16px)',
        right: 'calc(50% + 16px)',
    },
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: colorSecondary,
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: colorSecondary,
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
        borderTopWidth: 3,
        borderRadius: 1,
    },
}));

const QontoStepIconRoot = styled('div')(({ theme, ownerState }) => ({
    color: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#eaeaf0',
    display: 'flex',
    height: 22,
    alignItems: 'center',
    ...(ownerState.active && {
        color: colorSecondary,
    }),
    '& .QontoStepIcon-completedIcon': {
        color: colorSecondary,
        zIndex: 1,
        fontSize: 18,
    },
    '& .QontoStepIcon-circle': {
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: 'currentColor',
    },
}));

function QontoStepIcon(props) {
    const { active, completed, className } = props;

    return (
        <QontoStepIconRoot ownerState={{ active }} className={className}>
            {completed ? (
                <Check className="QontoStepIcon-completedIcon" />
            ) : (
                <div className="QontoStepIcon-circle" />
            )}
        </QontoStepIconRoot>
    );
}

const STEP = {
    set_variables: 'set_variables',
    create_app: 'create_app',
    frontend_copy: 'frontend_copy',
    frontend_build_and_deploy: 'frontend_build_and_deploy',
    events_copy: 'events_copy',
    events_build_and_deploy: 'events_build_and_deploy',
    set_secrets: 'set_secrets',
}
function replaceVariables(text, variables, rx = /\{\{\s*variables\.([\w\s]+)\s*\}\}/g) {
    return text.replace(rx, (match, key) => {
        if (variables.hasOwnProperty(key.toLowerCase())) {
            return variables[key.toLowerCase()];
        }
        return match;
    });
}

const getSecrets = ({app, installRequiredServices, installOptionalServices}) => {
    const appAllInstallableServices = [...(installRequiredServices ?? []), ...(installOptionalServices ?? [])];
    let secrets = app.secrets || [];

    if (appAllInstallableServices?.length) {
        const installSecrets = appAllInstallableServices.filter(o => o?.secrets?.length).map(o => o.secrets).flat();
        if (installSecrets?.length) {
            secrets = [...secrets, ...installSecrets]
        }
    }

    return secrets
}

export const AppInstallStepper = ({ appId, app, activeStep, setActiveStep, userProfile }) => {
    const {setSystemAlert} = useContext(AlertContext);
    const [installationData, setInstallationData] = useState({
        appData: app,
        variables: {},
        secrets: {},
        frontendFiles: null,
        eventsFiles: null,
    });

    const [steps, setSteps] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [KBID, setKBID] = useState(null);
    const [KBKey, setKBKey] = useState(null);
    const [isMenuOpened, setIsMenuOpened] = useState(false);
    const [hide, setHide] = useState(false);
    const [installRequiredServices, setInstallRequiredServices] = useState(null);
    const [installOptionalServices, setInstallOptionalServices] = useState(null);
    const contentMinHeight = '350px';
    const contentMaxHeight = '800px';
    const [expandedDescription, setExpandedDescription] = useState(false);
    const [expandedProtection, setExpandedProtection] = useState(false);

    const handleExpandDescriptionClick = () => {
        setExpandedDescription(!expandedDescription);
    };

    const handleExpandProtectionClick = () => {
        setExpandedProtection(!expandedProtection);
    };

    useEffect(() => {
        if (activeStep === 0 && getQueryParamValue("installServices")) {
            try {
                const {requiredServices, optionalServices} = JSON.parse(localStorage.getItem('installServices'))

                setInstallRequiredServices(requiredServices || []);
                setInstallOptionalServices(optionalServices || []);
            } catch (e) {
                console.log('Failed to parse selected services')
            }

        }
    }, [activeStep]);

    useEffect(() => setLoading(true), [loadingMessage]);

    const handleNext = (currentStepId, kbid) => {
        if (steps.findIndex(step => step.id === currentStepId) === (steps.length - 1)) {
            setHide(true);
            setLoadingMessage('Navigating to application');

            const phoneNotVerified = userProfile?.phoneVerificationStatus === PHONE_VERIFICATION_STATUS.NOT_VERIFIED;

            navigateKBSubdomain((kbid || KBID), phoneNotVerified)
            return;
        } else {
            setActiveStep((prevActiveStep) => prevActiveStep + 1)
        }
    }

    const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1)

    useEffect(() => {
        // wait for install data
        if (getQueryParamValue("installServices") && localStorage.getItem('installServices') && (!installRequiredServices || !installOptionalServices)) return

        if (isMobile) {
            setIsMenuOpened(false);
        } else {
            setIsMenuOpened(true);
        }

        const variables = parseJSON(app.variables);
        const secrets = getSecrets({app, installRequiredServices, installOptionalServices})
        const mySteps = [];

        setLoadingMessage('Prepare App');

        if (variables?.length > 0) {
            // set variables and aliases
            setInstallationData(prevData => ({
                ...prevData,
                variables: variables.reduce((acc, { name, default: defaultValue, aliases }) => {
                    const value = typeof prevData.variables?.[name] === 'string' ? prevData.variables[name] : defaultValue;
                    const aliasUpdates = Object.fromEntries((aliases || []).map(alias => [alias, value]));
                    return {...acc, [name]: value, ...aliasUpdates};
                }, { ...prevData.variables })
            }));

            mySteps.push({label: 'Set Variables', id: STEP.set_variables})
        }

        if (secrets?.length > 0) {
            mySteps.push({label: 'Set Secrets', id: STEP.set_secrets});
        }

        mySteps.push({label: 'Create App', id: STEP.create_app});

        // Define an async function to fetch files
        const prepareSteps = async () => {
            const skipFrontend = getQueryParamValue('skipFrontend') || app?.installation?.skipFrontend;
            const skipEvents = getQueryParamValue('skipEvents') || app?.installation?.skipEvents;

            try {
                const [frontendFiles, eventsFiles] = await Promise.all([
                    skipFrontend ? [] : listPublicAppFiles(app.appId, 'Frontend'),
                    skipEvents ? [] : listPublicAppFiles(app.appId, 'Events')
                ]);

                const frontendModules = extractModuleNames(frontendFiles);
                const eventsModules = extractModuleNames(eventsFiles);

                const frontendModulesWithoutBuilds = frontendModules.filter(module => {
                    const bundleFileName = `public-app-files/${app.appId}/Frontend/dist/${module}.bundle.js`;
                    return !frontendFiles.includes(bundleFileName);
                });

                const eventsModulesWithoutBuilds = eventsModules.filter(module => {
                    const bundleFileName = `public-app-files/${app.appId}/Events/dist/${module}/index.js`;
                    return !eventsModules.includes(bundleFileName);
                });

                if (frontendFiles?.length > 0) {
                    mySteps.push({label: 'Generate Frontend', id: STEP.frontend_copy});

                    if (frontendModulesWithoutBuilds?.length > 0) {
                        mySteps.push({label: 'Build & Deploy Frontend', id: STEP.frontend_build_and_deploy, frontendModulesWithoutBuilds});
                    }
                }

                if (eventsFiles?.length > 0) {
                    mySteps.push({label: 'Generate Back-end', id: STEP.events_copy});
                    if (eventsModulesWithoutBuilds?.length > 0) {
                        mySteps.push({label: 'Build & Deploy Back-end', id: STEP.events_build_and_deploy, eventsModulesWithoutBuilds});
                    }
                }

                setSteps(mySteps)
                setLoading(false);
            } catch (error) {
                // Handle errors here
                console.error('Error fetching files:', error);
            }
        };

        prepareSteps();

    }, [app, installRequiredServices, installOptionalServices]);


    useEffect(() => {
        // wait for install data
        if (getQueryParamValue("installServices") && localStorage.getItem('installServices') && (!installRequiredServices || !installOptionalServices)) return

        const appAllInstallableServices = [...(installRequiredServices ?? []), ...(installOptionalServices ?? [])];

        const run = async () => {
            const currentStepId = steps?.[activeStep]?.id
            let kbid = null;
            if (currentStepId === STEP.create_app) {
                setLoading(true);
                if (!KBID) {
                    downloadApp(appId)
                    // check if app exists and no installServices
                    const res = await createAppEntry(
                        appAllInstallableServices?.length ? undefined : appId
                    );

                    if (res?.kbId) {
                        kbid = res?.kbId
                        setKBID(kbid);
                    } else if (res?.existingKBId) {
                        // Navigate to existing
                        setHide(true);
                        setLoadingMessage('Navigating to existing application');
                        navigateKBSubdomain(res?.existingKBId)
                        return;
                    }

                }

                handleNext(currentStepId, kbid);

                setLoading(false);

            }
            else if (currentStepId === STEP.frontend_copy) {
                setLoadingMessage('Copying Frontend files...');
                try {
                    await cloneFiles('frontend', 'Frontend', appId, KBID)
                    handleNext(currentStepId); // Optionally move to the next step
                } catch (error) {
                    console.error('Error copying frontend files:', error);
                }

                setLoading(false);
            } else if (currentStepId === STEP.events_copy) {
                const hasNonEmptySecrets = Object.values(installationData.secrets).some(value => value !== '');
                if (hasNonEmptySecrets && KBKey) {
                    setLoadingMessage('Insert Encrypted Secrets...');
                    for (const secretName in installationData.secrets) {
                        const secretValue = installationData.secrets[secretName];
                        await createSecret(secretName, encryptKBItem(secretValue, KBID, KBKey), KBID);
                    }
                    setInstallationData((prevData) => ({...prevData, secrets: {}}));
                    setKBKey(null);
                }

                setLoadingMessage('Deploy Chat Events...');

                try {
                    await cloneFiles('functions', 'Events', appId, KBID)

                    const servicesWithCodeSnippets = appAllInstallableServices
                        .filter(o => o.type === appServiceTypes["backend-chat-function"] && o.codeSnippet)

                    if (servicesWithCodeSnippets?.length) {
                        let codeSnippetsString = ``;
                        servicesWithCodeSnippets.forEach(({codeSnippet, selectedServiceId}) => {
                            codeSnippetsString += '\n\n' + codeSnippet.replace(/\{\{\s*openkbsService\.id\s*\}\}/g, selectedServiceId) + '\n\n'
                        })

                        const actionsFileContent = `
export const getActions = (meta) => {
    return [
${codeSnippetsString}
    ];
}
`
                        await copyFile('functions', 'Events', 'actions.js', actionsFileContent, KBID)
                    }

                    handleNext(currentStepId); // Optionally move to the next step
                } catch (error) {
                    console.error('Error copying frontend files:', error);
                }

                setLoading(false);
            } else if (currentStepId === STEP.frontend_build_and_deploy) {
                const {frontendModulesWithoutBuilds} =  steps?.[activeStep];
                setLoading(true);

                try {
                    for (let module of frontendModulesWithoutBuilds) {
                        setLoadingMessage(`Build & Deploy ${module}...`);
                        await buildWebpackPackage("frontend", KBID, module)
                    }

                    setLoading(false);
                    handleNext(currentStepId); // Optionally move to the next step
                } catch (error) {
                    // Handle error (e.g., show an alert or message)
                    setLoading(false);
                    console.error('Error build and deploy frontend files:', error);
                }
            } else if (currentStepId === STEP.events_build_and_deploy) {
                const {eventsModulesWithoutBuilds} =  steps?.[activeStep];
                setLoading(true);

                try {
                    for (let module of eventsModulesWithoutBuilds) {
                        setLoadingMessage(`Build & Deploy ${module} Event...`);
                        await buildNodePackage("functions", KBID, module)
                    }

                    setLoading(false);
                    handleNext(currentStepId); // Optionally move to the next step
                } catch (error) {
                    // Handle error (e.g., show an alert or message)
                    setLoading(false);
                    console.error('Error build and deploy events files:', error);
                }
            }
        };

        run();
    }, [activeStep, steps, installRequiredServices, installOptionalServices]);

    const createAppEntry = async (installedAppId) => {
        let msgCreateApp = 'Creating application';

        const {appData, variables, eventsFiles} = installationData;

        const copyData = deepClone(appData)

        copyData.kbTitle = replaceVariables(copyData.kbTitle, variables)
        copyData.kbTitle = replaceVariables(copyData.kbTitle, variables, /\{\s*([\w\s]+)\s*\}/g) // short syntax
        copyData.kbInstructions = replaceVariables(copyData.kbInstructions, variables)
        copyData.kbDescription = replaceVariables(copyData.kbDescription, variables)

        let { kbTitle, kbDescription, kbInstructions, chatVendor, chatEndpoint, model,
            OpenAIAPIKey, voiceId, videoVendor, videoVoiceId, inputTools, frontendTokenLimit,
            itemTypes, embeddingDimension, embeddingModel, searchEngine
        } = copyData

        kbTitle = kbTitle.split(':')[0];

        if (installRequiredServices?.length || installOptionalServices?.length) {
            const appAllInstallableServices = [...(installRequiredServices ?? []), ...(installOptionalServices ?? [])];

            const servicesToInstall = deepClone(appAllInstallableServices.map(rs => {
                return {
                    id: rs.selectedServiceId,
                    ...MyServices[rs.selectedServiceId]
                }
            }));

            const installedServices = [];

            // Install Services
            let i = -1;
            appAllInstallableServices?.forEach((installableService) => {
                const index = servicesToInstall.findIndex(service => service.interfaces.includes(installableService.interface));
                if (index !== -1) {
                    i++;
                    const serviceFound = servicesToInstall.splice(index, 1)[0];
                    installedServices.push(serviceFound)

                    const serviceName = serviceFound?.shortName ? serviceFound.shortName : serviceFound.name

                    if (i === 0) {
                        kbTitle += ' with ' + serviceName
                    } else if (i === 1) {
                        kbTitle += ' and ' + serviceName
                    }

                    if (installableService.instructionSnippet && i === 0) kbInstructions = '';

                    if (installableService.instructionSnippet) {
                        kbInstructions += (kbInstructions ? '\n\n' : '') + installableService.instructionSnippet + '\n\n'
                    }

                    if (installableService.type === appServiceTypes["main-chat-model"] &&
                        installableService.interface === interfaces['text-generation']) {
                        // override default Chat model
                        model = serviceFound.id
                        chatVendor = serviceFound.vendor
                    } else if (installableService.interface === interfaces['items-search-engine']) {
                        searchEngine = capitalizeFirstLetter(serviceFound.id)
                    } else if (installableService.type === appServiceTypes["chat-input-tool"]) {
                        // find the inputTool by serviceId
                        const inputTool = Object.keys(InputTools).find(k => InputTools[k]?.serviceId === serviceFound.id);
                        if (!inputTools.includes(inputTool)) {
                            inputTools.push(inputTool);
                        }
                    } else if (installableService.type === appServiceTypes["image-editor-generation-model"] &&
                        installableService.interface === interfaces['text-to-image']) {
                        const imageEditorTool = parseJSONTool(inputTools, 'imageEditor');
                        imageEditorTool.generationModels = [serviceFound.id]
                        inputTools = inputTools
                            .map(tool => parseJSON(tool)?.type === 'imageEditor' ? JSON.stringify(imageEditorTool) : tool)
                    }
                }
            });

            if (appAllInstallableServices?.length) {
                msgCreateApp = `Creating ${kbTitle} with ` + installedServices.map(s => MyServices[s.id].name).join(', ')
                    .replace(/, ([^,]*)$/, ' and $1');
            }

            setLoadingMessage(msgCreateApp);
        }

        let key = generateKey('');

        setKBKey(key);

        kbTitle = kbTitle.split(':')[0];

        const encryptedTitle = encrypt(kbTitle, key);
        const encryptedDescription = encrypt(kbDescription, key);
        const encryptedInstructions = encrypt(kbInstructions, key);
        const encryptedWalletPrivateKey = encrypt(userProfile.walletPrivateKey, key);


        const injectedModel = getQueryParamValue(getQueryParams.injectedModel);
        const injectedModelService = ChatModels?.[injectedModel]

        if (injectedModel && injectedModelService) {
            chatVendor = injectedModelService.vendor
            model = injectedModel
        }

        const { kbId, existingKBId } = await saveKB({
            action: 'create',
            kbTitle: encryptedTitle,
            pwaName: kbTitle,
            kbDescription: encryptedDescription,
            kbInstructions: encryptedInstructions,
            chatVendor: chatVendor,
            chatEndpoint: chatEndpoint,
            model: model,
            OpenAIAPIKey: OpenAIAPIKey,
            voiceId: voiceId,
            videoVendor: videoVendor,
            videoVoiceId: videoVoiceId,
            key: key,
            fileData: null,
            inputTools: inputTools,
            frontendTokenLimit: frontendTokenLimit,
            accountId: userProfile.accountId,
            walletPublicKey: userProfile.walletPublicKey,
            walletPrivateKey: encryptedWalletPrivateKey,
            itemTypes,
            embeddingDimension,
            embeddingModel,
            searchEngine,
            installedAppId
        });

        if (existingKBId) return { existingKBId }

        await copyAppImage(appId, kbId);

        return {kbId};
    }

    const handleSecretChange = (secret, value) => {
        setInstallationData((prevData) => {
            return {
                ...prevData,
                secrets: {
                    ...prevData.secrets,
                    [secret.name]: value,
                }
            };
        });
    };

    const handleVariableChange = (variable, value) => {
        setInstallationData((prevData) => {
            // Create an object with all aliases set to the value
            const aliases = (variable.aliases || []).reduce((acc, alias) => {
                acc[alias] = value;
                return acc;
            }, {});

            return {
                ...prevData,
                variables: {
                    ...prevData.variables,
                    [variable.name]: value,
                    ...aliases
                }
            };
        });
    };

    const getStepContent = (stepId) => {
        switch (stepId) {
            case STEP.set_variables:
                return (
                    <div>
                        {parseJSON(app.variables)?.map?.((variable) => (
                            variable.options && variable.options.length > 0 ? (
                                <Autocomplete
                                    key={variable.name}
                                    options={variable.options}
                                    freeSolo={false}
                                    value={installationData?.variables[variable.name.toLowerCase()] || variable.default || ''}
                                    onInputChange={(event, newValue) => {
                                        handleVariableChange(variable, newValue);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            name={variable.name}
                                            label={<TypingLabel text={variable.description} speed={20} />}
                                            placeholder={variable.placeholder}
                                            margin="normal"
                                            fullWidth
                                        />
                                    )}
                                />
                            ) : (
                                <TextField
                                    name={variable.name}
                                    key={variable.name}
                                    label={variable.description}
                                    type="text"
                                    value={(typeof installationData?.variables?.[variable.name] === 'string')
                                        ? installationData.variables[variable.name]
                                        : variable.default
                                    }
                                    onChange={(e) => handleVariableChange(variable, e.target.value)}
                                    margin="normal"
                                    placeholder={variable.placeholder}
                                    rows={variable?.rows ? variable?.rows : undefined}
                                    multiline
                                    fullWidth
                                />
                            )
                        ))}
                    </div>
                )
            case STEP.set_secrets:
                const secrets = getSecrets({ app, installRequiredServices, installOptionalServices });

                return (
                    <Box pt={3} pb={2}>
                        <Box p={3} border={1} borderColor="grey.300" borderRadius={4} boxShadow={3}>
                            <Typography variant="h6" gutterBottom>Enter Required Secrets</Typography>

                            {secrets?.map?.((secret) => (
                                <SecretInput
                                    key={secret.name}
                                    secret={secret}
                                    value={(typeof installationData?.secrets?.[secret.name] === 'string') ? installationData.secrets[secret.name] : ''}
                                    onChange={(e) => handleSecretChange(secret, e.target.value)}
                                />
                            ))}

                            {app.secretsDescription && app.secretsTitle && (
                                <Box pt={2} pb={2}>
                                    <Accordion expanded={expandedDescription} onChange={handleExpandDescriptionClick}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="description-content"
                                            id="description-header"
                                        >
                                            <Typography variant="body2" color="textSecondary">
                                                {app.secretsTitle}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography variant="body2" color="textSecondary" align="left">
                                                <ReactMarkdown>{app.secretsDescription}</ReactMarkdown>
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                </Box>
                            )}
                            <Box pt={2} pb={2}>
                                <Accordion expanded={expandedProtection} onChange={handleExpandProtectionClick}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls="protection-content"
                                        id="protection-header"
                                    >
                                        <Typography variant="body2" color="textSecondary">
                                            How we protect your secrets?
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography variant="body2" color="textSecondary" align="left">
                                            <ReactMarkdown>You secrets are double-encrypted—first with your private AES key (before leaving the browser) and then with a public asymmetric key (before being stored)—ensuring that the storage service cannot decrypt them. Only the code execution service, upon your AES key authorization, can decrypt and execute them.</ReactMarkdown>
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        </Box>
                    </Box>
                );
            case STEP.frontend_copy:
                return <></>;
            case STEP.frontend_build_and_deploy:
                return <></>;
            default:
                return <></>;
        }
    };

    const renderLoadingOverlay = () => (
        <Backdrop open={loading} style={{ color: '#fff', zIndex: 9999, flexDirection: 'column', paddingLeft: (isMenuOpened ? 300 : 0) }}>
            <CircularProgress color="inherit" />
            <div style={{ marginTop: '20px', textAlign: 'center' }}>{loadingMessage}</div>
        </Backdrop>
    );

    if (hide) return renderLoadingOverlay();

    let nextButtonName = 'Next';
    let nextButtonDisabled = false;

    if (steps?.[activeStep]?.id === STEP.set_secrets &&
        getSecrets({app, installRequiredServices, installOptionalServices})?.find(o => o.optional)) {
        if (!Object.values(installationData.secrets).some(value => value !== '')) {
            nextButtonName = 'Skip'
        } else if (Object.values(installationData.secrets).some(value => value === '')) {
            nextButtonDisabled = true
        }

    }

    return (
        <div>
            {loading && renderLoadingOverlay()}
            {<Stepper activeStep={activeStep} alternativeLabel connector={<QontoConnector />} style={{ minHeight: '72px' }}>
                {steps.length > 0 ? (
                    steps.map((step) => (
                        <Step key={step.id}>
                            <StepLabel StepIconComponent={QontoStepIcon}>
                                <Typography style={{fontSize: isMobile? 10 : undefined}}>{step.label}</Typography>
                            </StepLabel>
                        </Step>
                    ))
                ) : (
                    <Step></Step>
                )}
            </Stepper>}
            <div style={{ maxWidth: isMobile ? '100%' : '70%', margin: 'auto' }}>
                <div style={{ minHeight: contentMinHeight, maxHeight: contentMaxHeight, overflow: 'auto' }}>
                    {steps?.[activeStep]?.id && getStepContent(steps[activeStep].id)}
                </div>
                {!loading && activeStep <= steps.length && (
                    <div style={{ paddingTop: '16px', display: 'flex', justifyContent: 'space-between', gap: '20%' }}>
                        <Button onClick={handleBack}
                            size={isMobile ? 'small' : 'large'}
                            style={{ flexGrow: 1 }}
                        >
                            Back
                        </Button>
                        <Button variant="contained"
                            color="primary"
                            onClick={handleNext}
                            disabled={nextButtonDisabled}
                            size={isMobile ? 'small' : 'large'}
                            style={{ flexGrow: 1 }}
                        >
                            {activeStep === (steps.length - 1) ? 'Finish' : nextButtonName}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};